interface PdfWriter {
  write(data: Blob): Promise<void>;
  close(): Promise<void>;
  abort(): Promise<void>;
}

type PickerWindow = Window & {
  showSaveFilePicker?: (options: {
    id: string;
    suggestedName: string;
    types: { description: string; accept: Record<string, string[]> }[];
  }) => Promise<{ createWritable(): Promise<PdfWriter> }>;
};

export function canChooseInvoicePdfLocation(): boolean {
  return window.isSecureContext && typeof (window as PickerWindow).showSaveFilePicker === 'function';
}

// Call directly from the Save as click: awaiting a network request first loses
// the browser's user activation. The blob is already the server's saved copy.
export async function saveInvoicePdf(blob: Blob, filename: string): Promise<'saved' | 'cancelled'> {
  const picker = (window as PickerWindow).showSaveFilePicker;
  if (!canChooseInvoicePdfLocation() || !picker) throw new Error('Save as is not available in this browser.');
  let handle;
  try {
    handle = await picker.call(window, {
      id: 'keystone-invoice-pdf',
      suggestedName: filename,
      types: [{ description: 'PDF document', accept: { 'application/pdf': ['.pdf'] } }],
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') return 'cancelled';
    throw error;
  }
  const writer = await handle.createWritable();
  try {
    await writer.write(blob);
    await writer.close();
  } catch (error) {
    // Preserve the original write error even if abort also fails.
    await writer.abort().catch(() => undefined);
    throw error;
  }
  return 'saved';
}
